import React, { useRef, useMemo, useEffect } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

// Define relative center
const relativeImpellerCenter = new THREE.Vector3(1, -0.5, 0);

// Constants that don't need to be reactive
const baseSpeed = 0.8;
const maxLifetime = 6.0;
const intakeWorldOffsetX = 4;
const intakeSpreadZ = 5;
const attractionForce = 2.5;
const damping = 0.25;
const distanceThreshold = 0.15;
const outblowDirection = new THREE.Vector3(-1, 0, 0).normalize();
const outblowSpeed = 2.5;
const outblowSpread = 0.02;
const outflowRandomnessFactor = 0.03;
const outflowSpeedVariation = 0.5;

const WaterParticles = ({ 
  meshGroups, 
  waterSurfaceY = 0.5, 
  modelPosition = [0,0,0], 
  particleColor = '#88ccff',
  particleSize = 1.0,
  particleDensity = 1.0,
  particleIntakeYOffset = -0.2
}) => {
  const pointsRef = useRef();
  const baseParticleCount = 1000;
  const particleCount = Math.max(10, Math.round(baseParticleCount * particleDensity));

  // Get the world position as a Vector3
  const worldModelPos = useMemo(() => {
    const pos = Array.isArray(modelPosition) 
      ? new THREE.Vector3(modelPosition[0], modelPosition[1], modelPosition[2])
      : modelPosition;
    // Log the calculated world position once - REMOVED for clarity
    // console.log(`WaterParticles worldModelPos: x=${pos.x}, y=${pos.y}, z=${pos.z}`); 
    return pos;
  }, [modelPosition]);
  
  // Create geometry
  const geometry = useMemo(() => {
    // --- Log Geometry Params --- 
    console.log(`WaterParticles Geometry useMemo: count=${particleCount}, densityProp=${particleDensity}, intakeYProp=${particleIntakeYOffset}`);
    // -------------------------
    const geo = new THREE.BufferGeometry();
    
    // Create attributes
    const positions = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);
    const lifetimes = new Float32Array(particleCount);
    const states = new Float32Array(particleCount);
    
    // Calculate water line in world space
    const waterLineY = worldModelPos.y + waterSurfaceY;
    // console.log(`Water line is at Y=${waterLineY}, particles will spawn below this`); // Removed log

    // --- Define Spawn Center --- 
    const spawnCenter = new THREE.Vector3().copy(worldModelPos);
    spawnCenter.x += intakeWorldOffsetX; // Center the spawn area in front
    // We could also add Y/Z offsets here if needed, but let's start simple.
    console.log(`Calculated Spawn Center (Initial): x=${spawnCenter.x}, y=${spawnCenter.y}, z=${spawnCenter.z}`);
    // -------------------------
    
    // Initialize particles AROUND Spawn Center
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      
      // Position around spawnCenter
      positions[i3] = spawnCenter.x + (Math.random() - 0.5) * 1.0; // Spread around spawnCenter.x
      
      // GUARANTEED to be below water surface
      const depthBelowWater = 0.3 + Math.random() * 0.7; // 0.3 to 1.0 units below water
      positions[i3 + 1] = waterLineY - depthBelowWater; // Subtract to go below water line
      
      positions[i3 + 2] = spawnCenter.z + (Math.random() - 0.5) * 2.0; // Spread around spawnCenter.z
      
      // Initial velocity still targets impellerCenter
      const pos = new THREE.Vector3(positions[i3], positions[i3 + 1], positions[i3 + 2]);
      const target = new THREE.Vector3().copy(worldModelPos).add(relativeImpellerCenter);
      target.y += particleIntakeYOffset;
      const dir = new THREE.Vector3().subVectors(target, pos).normalize();
      
      // Add randomness
      dir.x += (Math.random() - 0.5) * 0.3;
      dir.y += (Math.random() - 0.5) * 0.2;
      dir.z += (Math.random() - 0.5) * 0.3;
      dir.normalize();
      
      // Set velocity
      const speed = baseSpeed * (0.5 + Math.random() * 0.8);
      velocities[i3] = dir.x * speed;
      velocities[i3 + 1] = dir.y * speed;
      velocities[i3 + 2] = dir.z * speed;
      
      // Set lifetime and state
      lifetimes[i] = Math.random() * maxLifetime;
      states[i] = 0;
    }
    
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3));
    geo.setAttribute('lifetime', new THREE.BufferAttribute(lifetimes, 1));
    geo.setAttribute('state', new THREE.BufferAttribute(states, 1));
    
    return geo;
  }, [particleCount, worldModelPos, waterSurfaceY, particleDensity, particleIntakeYOffset]);

  // Create shader material
  const material = useMemo(() => {
    // --- Log Material Params --- 
    // console.log(`WaterParticles Material useMemo: sizeProp=${particleSize}, colorProp=${particleColor}`); // Remove log
    // -------------------------
    return new THREE.ShaderMaterial({
      uniforms: {
        uMaxLifetime: { value: maxLifetime },
        uParticleSize: { value: particleSize }, // << Restore prop value
        uParticleColor: { value: new THREE.Color(particleColor) } // << Restore prop value
      },
      vertexShader: `
        attribute float lifetime;
        varying float vLifetime;
        uniform float uMaxLifetime;
        uniform float uParticleSize;
        
        void main() {
          vLifetime = lifetime;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          
          float lifeRatio = clamp(vLifetime / uMaxLifetime, 0.0, 1.0);
          gl_PointSize = (1.0 + 3.0 * lifeRatio) * uParticleSize;
          
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying float vLifetime;
        uniform float uMaxLifetime;
        uniform vec3 uParticleColor;
        
        void main() {
          float alpha = clamp(vLifetime / uMaxLifetime, 0.0, 1.0);
          if (alpha < 0.01) discard;
          gl_FragColor = vec4(uParticleColor, alpha * 0.8); // << Restore original alpha calculation
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
  }, [particleColor, particleSize]);
  
  // Update material uniforms when props change
  useEffect(() => {
    if (pointsRef.current && pointsRef.current.material) {
      const mat = pointsRef.current.material;
      mat.uniforms.uParticleSize.value = particleSize;
      mat.uniforms.uParticleColor.value.set(particleColor);
    }
  }, [particleColor, particleSize]);

  // Animation loop
  useFrame((state, delta) => {
    // --- ADD THIS GUARD CLAUSE ---
    if (delta <= 0) return; // Prevent updates if delta is zero or negative
    // -----------------------------
    
    // --- RE-ENABLE CONSOLE LOG FOR DEBUGGING --- 
    // console.log('WaterParticles useFrame executing, delta:', delta); // << REMOVE THIS LOG
    // ------------------------------------

    if (!pointsRef.current) return;
    const geo = pointsRef.current.geometry;
    if (!geo || !geo.attributes) return;
    
    // --- RE-ENABLE MOVEMENT LOGIC --- 
    const positions = geo.attributes.position.array;
    const velocities = geo.attributes.velocity.array;
    const lifetimes = geo.attributes.lifetime.array;
    const states = geo.attributes.state.array;
    
    const worldPos = Array.isArray(modelPosition) 
      ? new THREE.Vector3(modelPosition[0], modelPosition[1], modelPosition[2]) 
      : modelPosition;
    
    const waterLineY = worldPos.y + waterSurfaceY;
    
    // Calculate impeller center - Attraction point
    const impellerCenter = new THREE.Vector3().copy(worldPos).add(relativeImpellerCenter);
    impellerCenter.y += particleIntakeYOffset;

    // --- Calculate Spawn Center (for respawn) --- 
    const spawnCenter = new THREE.Vector3().copy(worldPos);
    spawnCenter.x += intakeWorldOffsetX; // Center the respawn area in front
    // --------------------------------------------

    const pos = new THREE.Vector3();
    const vel = new THREE.Vector3();
    const dir = new THREE.Vector3();
    
    for (let i = 0; i < particleCount; i++) {
        // Copied existing movement and state logic back in
      const i3 = i * 3;
      
      if (i >= lifetimes.length || i3 + 2 >= positions.length) continue;
      
      lifetimes[i] -= delta;
      
      if (lifetimes[i] <= 0) {
          // Respawn AROUND Spawn Center
          const spawnSpreadX = 1.0; // How much to spread X around spawn center
          const spawnSpreadZ = intakeSpreadZ; // Use existing Z spread constant
        
          positions[i3]     = spawnCenter.x + (Math.random() - 0.5) * spawnSpreadX;
          positions[i3 + 2] = spawnCenter.z + (Math.random() - 0.5) * spawnSpreadZ; 
        
          // Y: Spawn slightly below the water line
          const depthBelowWater = 0.3 + Math.random() * 0.7;
          positions[i3 + 1] = waterLineY - depthBelowWater;
        
          // Ensure spawn is also below the main water surface line (redundant but safe)
        if (positions[i3 + 1] >= waterLineY) {
            positions[i3 + 1] = waterLineY - 0.1; 
        }
        
          // Reset velocity - Target the impellerCenter
        pos.set(positions[i3], positions[i3 + 1], positions[i3 + 2]);
          dir.copy(impellerCenter).sub(pos).normalize(); // Target impellerCenter
        
          // Add randomness to initial velocity direction
        dir.x += (Math.random() - 0.5) * 0.3;
        dir.y += (Math.random() - 0.5) * 0.2;
        dir.z += (Math.random() - 0.5) * 0.3;
          dir.normalize(); // Ensure it remains a direction vector
        
          // Set velocity magnitude
        const speed = baseSpeed * (0.5 + Math.random() * 0.8);
        velocities[i3] = dir.x * speed;
        velocities[i3 + 1] = dir.y * speed;
        velocities[i3 + 2] = dir.z * speed;
        
        // Reset lifetime and state
        lifetimes[i] = Math.random() * maxLifetime;
        states[i] = 0;
        
        continue;
      }
      
      // Get current position and state
      pos.set(positions[i3], positions[i3 + 1], positions[i3 + 2]);
      const state = states[i];
      
      if (state === 0) {
        // Intake state
          dir.copy(impellerCenter).sub(pos);
          const dist = dir.length();
          dir.normalize();
        vel.set(velocities[i3], velocities[i3 + 1], velocities[i3 + 2]);
          const force = dir.multiplyScalar(attractionForce * delta);
          vel.add(force);
        vel.multiplyScalar(1.0 - damping * delta);
        velocities[i3] = vel.x;
        velocities[i3 + 1] = vel.y;
        velocities[i3 + 2] = vel.z;
        
          if (dist < distanceThreshold) {
          // Switch to outflow state
          states[i] = 1;
          
          // Set initial outflow velocity with MORE randomness
          dir.copy(outblowDirection).normalize(); // Start with base direction
          
          // Add significant random offset
          dir.x += (Math.random() - 0.5) * outflowRandomnessFactor; 
          dir.y += (Math.random() - 0.5) * outflowRandomnessFactor; // Add randomness to Y as well
          dir.z += (Math.random() - 0.5) * outflowRandomnessFactor; // Add randomness to Z as well
          dir.normalize(); // Ensure it remains a direction vector
          
          // Add speed variation
          const speed = outblowSpeed * (1.0 + (Math.random() - 0.5) * outflowSpeedVariation);
          
          velocities[i3] = dir.x * speed;
          velocities[i3 + 1] = dir.y * speed;
          velocities[i3 + 2] = dir.z * speed;
            
            // Optional position nudge
            positions[i3] = impellerCenter.x + dir.x * 0.05;
            positions[i3 + 1] = impellerCenter.y + dir.y * 0.05;
            positions[i3 + 2] = impellerCenter.z + dir.z * 0.05;
        }
      } else {
        // Outflow state (state === 1)
        // Let particles continue with their current velocity
        // (We could add drag/gravity here if needed later)
        pos.x += velocities[i3] * delta;
        pos.y += velocities[i3 + 1] * delta;
        pos.z += velocities[i3 + 2] * delta;
        
        positions[i3] = pos.x;
        positions[i3 + 1] = pos.y;
        positions[i3 + 2] = pos.z;
      }
    
        // Update position based on velocity
        positions[i3] += velocities[i3] * delta;
        positions[i3 + 1] += velocities[i3 + 1] * delta;
        positions[i3 + 2] += velocities[i3 + 2] * delta;
        
        // Reset if particle flies above water during outblow
        if (positions[i3 + 1] > waterLineY && state === 1) {
           lifetimes[i] = 0; 
        }
    }
    
    geo.attributes.position.needsUpdate = true;
    geo.attributes.velocity.needsUpdate = true;
    geo.attributes.lifetime.needsUpdate = true;
    geo.attributes.state.needsUpdate = true;
    // --- END OF RE-ENABLED LOGIC ---
  });

  // Log impeller center once on mount for reference
  useEffect(() => {
    const worldPos = Array.isArray(modelPosition) 
      ? new THREE.Vector3(modelPosition[0], modelPosition[1], modelPosition[2]) 
      : modelPosition;
    const impellerCenter = new THREE.Vector3().copy(worldPos).add(relativeImpellerCenter);
    impellerCenter.y += particleIntakeYOffset;
    // console.log(`WaterParticles calculated impellerCenter: x=${impellerCenter.x}, y=${impellerCenter.y}, z=${impellerCenter.z}`); // << Remove Log
  }, [modelPosition, relativeImpellerCenter, particleIntakeYOffset]); // Recalculate if these change

  // --- Reintroduce key to force remount on critical prop changes --- 
  const instanceKey = `particles-${particleColor}-${particleSize}-${particleDensity}-${particleIntakeYOffset}`;
  
  return (
    <points ref={pointsRef} key={instanceKey} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={geometry.attributes.position.array}
          count={geometry.attributes.position.count}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-velocity"
          array={geometry.attributes.velocity.array}
          count={geometry.attributes.velocity.count}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-lifetime"
          array={geometry.attributes.lifetime.array}
          count={geometry.attributes.lifetime.count}
          itemSize={1}
        />
        <bufferAttribute
          attach="attributes-state"
          array={geometry.attributes.state.array}
          count={geometry.attributes.state.count}
          itemSize={1}
        />
      </bufferGeometry>
      <shaderMaterial
        uniforms={material.uniforms}
        vertexShader={material.vertexShader}
        fragmentShader={material.fragmentShader}
        transparent={true}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
};

export default WaterParticles; 